// src/components/subscription/UpgradePrompt.tsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Crown, ArrowRight, CheckCircle } from 'lucide-react';

export type UpgradePromptType = 'contacts' | 'groups' | 'ai' | 'todos' | 'storage';

interface UpgradePromptProps {
  type: UpgradePromptType;
  currentPlan: 'free' | 'medium';
  percentageUsed: number;
  onUpgrade: () => void;
  onDismiss: () => void;
  className?: string;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  type,
  currentPlan,
  percentageUsed,
  onUpgrade,
  onDismiss,
  className = ''
}) => {
  const [dismissed, setDismissed] = useState(false);
  
  // Check local storage for dismissed status on mount
  useEffect(() => {
    const dismissedPrompts = JSON.parse(localStorage.getItem('dismissedUpgradePrompts') || '{}');
    const now = new Date().getTime();
    
    // Check if this prompt type was dismissed in the last 24 hours
    if (dismissedPrompts[type] && dismissedPrompts[type] > now - 24 * 60 * 60 * 1000) {
      setDismissed(true);
    }
  }, [type]);
  
  // Handle dismissal with 24-hour suppression
  const handleDismiss = () => {
    // Save dismissal timestamp to local storage
    const dismissedPrompts = JSON.parse(localStorage.getItem('dismissedUpgradePrompts') || '{}');
    dismissedPrompts[type] = new Date().getTime();
    localStorage.setItem('dismissedUpgradePrompts', JSON.stringify(dismissedPrompts));
    
    setDismissed(true);
    onDismiss();
  };
  
  // If dismissed, don't show the prompt
  if (dismissed) {
    return null;
  }
  
  // Get feature-specific content
  const getFeatureContent = () => {
    switch (type) {
      case 'contacts':
        return {
          title: 'Running out of contacts',
          message: `You've used ${percentageUsed}% of your contact limit. Upgrade to ${currentPlan === 'free' ? 'Medium' : 'Premium'} to add more contacts.`,
          benefit: currentPlan === 'free' ? '200 contacts' : '1000 contacts'
        };
      case 'groups':
        return {
          title: 'Group limit reached',
          message: `You've used ${percentageUsed}% of your group limit. Upgrade to ${currentPlan === 'free' ? 'Medium' : 'Premium'} to create more groups.`,
          benefit: currentPlan === 'free' ? '20 groups' : '50 groups'
        };
      case 'ai':
        return {
          title: 'AI queries limited',
          message: `You've used ${percentageUsed}% of your monthly AI queries. Upgrade to ${currentPlan === 'free' ? 'Medium' : 'Premium'} for more AI interactions.`,
          benefit: currentPlan === 'free' ? '50 queries per month' : 'Unlimited AI queries'
        };
      case 'todos':
        return {
          title: 'Todo limit reached',
          message: `You've used ${percentageUsed}% of your todo limit. Upgrade to ${currentPlan === 'free' ? 'Medium' : 'Premium'} to add more todos.`,
          benefit: currentPlan === 'free' ? '500 todos' : '2000 todos'
        };
      case 'storage':
      default:
        return {
          title: 'Storage limit reached',
          message: `You've used ${percentageUsed}% of your storage limit. Upgrade to ${currentPlan === 'free' ? 'Medium' : 'Premium'} for more storage.`,
          benefit: currentPlan === 'free' ? '1GB storage' : '10GB storage'
        };
    }
  };
  
  const content = getFeatureContent();
  
  // Determine background color based on usage percentage
  const getBgColor = () => {
    if (percentageUsed >= 90) return 'bg-red-50 border-red-200';
    if (percentageUsed >= 75) return 'bg-amber-50 border-amber-200';
    return 'bg-blue-50 border-blue-200';
  };
  
  // Determine text color based on usage percentage
  const getTextColor = () => {
    if (percentageUsed >= 90) return 'text-red-800';
    if (percentageUsed >= 75) return 'text-amber-800';
    return 'text-blue-800';
  };
  
  // Determine icon color based on usage percentage
  const getIconColor = () => {
    if (percentageUsed >= 90) return 'text-red-500';
    if (percentageUsed >= 75) return 'text-amber-500';
    return 'text-blue-500';
  };

  return (
    <div className={`${getBgColor()} border rounded-lg p-4 ${className}`}>
      <div className="flex items-start">
        <AlertTriangle className={`${getIconColor()} flex-shrink-0 mr-3 mt-1`} size={20} />
        
        <div className="flex-1">
          <div className="flex justify-between">
            <h3 className={`font-medium ${getTextColor()}`}>{content.title}</h3>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </div>
          
          <p className={`text-sm mt-1 ${getTextColor()}`}>{content.message}</p>
          
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onUpgrade}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <Crown size={14} className={getIconColor()} />
              <span>Upgrade Now</span>
              <ArrowRight size={14} />
            </button>
            
            <div className="flex items-center text-xs text-gray-600">
              <CheckCircle size={14} className="mr-1" />
              <span>Get {content.benefit}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;